import { collection, doc, query, where, orderBy, limit as limitQuery, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from './firebaseCore';
import { TransactionType } from '../types';
import deposit_icon from '../assets/other/deposit.png';
import free_bet_icon from '../assets/other/free_bet.png';
import withdraw_icon from '../assets/other/withdraw.png';
import { fetchEurRate } from '../services/PriceService';

export const subscribeToUserTransactions = (
  userEmail: string,
  onUpdate: (transactions: TransactionType[]) => void,
  onError?: (error: Error) => void,
  limitCount?: number
): (() => void) => {
  if (!userEmail) {
    console.error("No userEmail provided");
    return () => { };
  }

  const transactionsRef = collection(db, 'user_transactions');
  const userDocRef = doc(db, 'users', userEmail);

  // Create base query
  let q = query(
    transactionsRef,
    where('user', '==', userDocRef),
    orderBy('TransxnDateTime', 'desc')
  );

  // Add limit if specified
  if (limitCount) {
    q = query(q, limitQuery(limitCount));
  }

  const unsubscribe = onSnapshot(
    q,
    async (querySnapshot) => {
      try {
        const eurRate = await fetchEurRate();
        if (!eurRate) {
          throw new Error('EUR rate unavailable');
        }

        const transactions: TransactionType[] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          try {
            // Convert asset amount to EUR using current rate
            const eurAmount = (data.amountEth || 0) * eurRate;

            // Determine which icon to use
            let transactionIcon;
            if (data.free_bet === true) {
              transactionIcon = free_bet_icon;
            } else {
              transactionIcon = data.deposit_withdraw ? deposit_icon : withdraw_icon;
            }

            transactions.push({
              id: doc.id || "dummy",
              name: data.deposit_withdraw ? 'Deposits' : 'Withdrawal',
              icon: transactionIcon,
              amount: eurAmount, // Using converted amount
              amount_eth: data.amountEth || 0,
              currency: data.Currency || 'EUR',
              direction: data.deposit_withdraw ? 'in' : 'out',
              transactionType: data.deposit_withdraw ? 'deposit' : 'withdrawal',
              TransxnDateTime: data.TransxnDateTime ? data.TransxnDateTime.toDate() : new Date(),
              free_bet: data.free_bet || false, // Default to false if field is absent
              txnHash: data.txnHash
              // fiatConverted: data.fiatConverted === false ? false : true
            });
          } catch (e) {
            console.error("Error processing document:", {
              docId: doc.id,
              error: e,
              rawData: data
            });
          }
        });

        onUpdate(transactions);
      } catch (error) {
        console.error("Error fetching ETH price or processing transactions:", error);
        if (onError) onError(error as Error);
      }
    },
    (error) => {
      console.error("Subscription error:", {
        error,
        userEmail,
        userDocRef
      });
      if (onError) onError(error);
    }
  );

  return unsubscribe;
};


export const subscribeToEnterDrawStatus = (
  userEmail: string,
  onUpdate: (canEnterDraw: boolean) => void,
  onError?: (error: Error) => void
): (() => void) => {
  if (!userEmail) {
    onUpdate(false);
    return () => { };
  }

  const userDocRef = doc(db, 'users', userEmail);

  const unsubscribe = onSnapshot(
    userDocRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        onUpdate(userData.enter_draw === true); // Explicitly check for true
      } else {
        onUpdate(false);
      }
    },
    (error) => {
      console.error("Error in enter draw status subscription:", error);
      if (onError) onError(error);
      onUpdate(false);
    }
  );

  return unsubscribe;
};


export const getLatestTransactionAmount = async (userEmail: string): Promise<string> => {
  if (!userEmail) {
    console.error("No userEmail provided for getLatestTransactionAmount");
    return "0";
  }

  try {
    const transactionsRef = collection(db, 'user_wallet_txn_hist');
    const userDocRef = doc(db, 'users', userEmail);

    // Primary query: order by server timestamp
    let querySnapshot;
    try {
      const q = query(
        transactionsRef,
        where('user', '==', userDocRef),
        where('type', '==', 'user_deposit'), // Deposit marker already stored as type
        orderBy('timestamp', 'desc'),
        limitQuery(1)
      );
      querySnapshot = await getDocs(q);
    } catch (err) {
      console.warn("[getLatestTransactionAmount] timestamp query failed, falling back to TransxnDateTime", err);
    }

    // Fallback query if timestamp ordering failed or returned empty
    if (!querySnapshot || querySnapshot.empty) {
      try {
        const fallbackQuery = query(
          transactionsRef,
          where('user', '==', userDocRef),
          where('type', '==', 'user_deposit'),
          orderBy('TransxnDateTime', 'desc'),
          limitQuery(1)
        );
        querySnapshot = await getDocs(fallbackQuery);
      } catch (err) {
        console.warn("[getLatestTransactionAmount] fallback query failed", err);
      }
    }

    if (querySnapshot && !querySnapshot.empty) {
      const latestTxn = querySnapshot.docs[0].data();

      const amount =
        latestTxn.amountEth ??
        latestTxn.amount ??
        latestTxn.amountETH ??
        latestTxn.AmountEth ??
        latestTxn.AmountETH ??
        0;
      // Ensure the amount is returned as a string
      return amount ? amount.toString() : "0";
    }

    return "0";
  } catch (error) {
    console.error("Error fetching latest transaction amount:", error);
    return "0";
  }
};